<template>
  <div>
    <v-container fluid>
      <div class="mt-3">
        <v-row justify="center" align="center">
          <v-col cols="12" sm="12" md="6">
            <v-card>
              <v-card-title class="body-2 v-card__subtitle">
                <v-icon x-small class="mr-2">mdi-counter</v-icon>
                Zusammenfassung der Rückmeldungen
              </v-card-title>

              <v-card-text>
                <v-chip
                  color="green lighten-4"
                  label
                  class="font-weight-medium green--text text--darken-4 mr-2 mb-2"
                  >{{
                    matrixStats.totalResponsesCount.complete
                  }}
                  Beantwortet</v-chip
                >
                <v-chip
                  color="orange lighten-4"
                  label
                  class="font-weight-medium orange--text text--darken-4 mr-2 mb-2"
                  >{{
                    matrixStats.totalResponsesCount.incomplete
                  }}
                  Unvollständig</v-chip
                >
                <v-chip
                  color="red lighten-4"
                  label
                  class="font-weight-medium red--text text--darken-4 mr-2 mb-2"
                  >{{
                    matrixStats.totalResponsesCount.notResponded
                  }}
                  Unbeantwortet</v-chip
                >
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" sm="12" md="6">
            <v-card>
              <v-card-title class="body-2 v-card__subtitle">
                <v-icon x-small class="mr-2">mdi-counter</v-icon>
                Besetzungsmöglichkeiten
              </v-card-title>

              <v-card-text>
                <v-chip
                  color="green lighten-4"
                  label
                  class="font-weight-medium green--text text--darken-4 mr-2 mb-2"
                  ><v-icon left small>mdi-check-circle</v-icon
                  >{{ matrixStats.possibleTimeSlots }} Terminoptionen
                  besetzbar</v-chip
                >
                <v-chip
                  color="red lighten-4"
                  label
                  class="font-weight-medium red--text text--darken-4 mr-2 mb-2"
                  ><v-icon left small>mdi-close-circle</v-icon
                  >{{ matrixStats.notPossibleTimeSlots }} Terminoptionen nicht
                  besetzbar</v-chip
                >
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" sm="12" md="12" hidden>
            <v-card v-if="requestResponsesMatchedComments.length > 0">
              <v-card-subtitle
                ><v-btn
                  class="mr-2"
                  icon
                  depressed
                  @click="
                    helpers.showResponseComments = !helpers.showResponseComments
                  "
                  ><v-icon>{{
                    helpers.showResponseComments
                      ? "mdi-chevron-up"
                      : "mdi-chevron-down"
                  }}</v-icon></v-btn
                >Kommentare<v-spacer></v-spacer
              ></v-card-subtitle>
              <v-expand-transition>
                <div v-show="helpers.showResponseComments">
                  <v-divider></v-divider>

                  <v-list-item
                    v-for="response in requestResponsesMatchedComments"
                    :key="response.user.uid"
                  >
                    <v-avatar
                      rounded
                      size="46"
                      :color="
                        $vuetify.theme.dark
                          ? 'rgba(255, 255, 255, 0.12)'
                          : 'grey lighten-2'
                      "
                      class="font-weight-medium mr-4"
                    >
                      {{ initials(response.user.displayName) }}
                    </v-avatar>

                    <v-list-item-content>
                      <v-list-item-title
                        class="font-weight-medium primary-text"
                        >{{ response.user.displayName }}</v-list-item-title
                      >
                      <v-list-item-subtitle class="text-wrap d-flex"
                        ><v-icon small class="mr-3">mdi-comment-text</v-icon
                        >{{ response.comment }}</v-list-item-subtitle
                      >
                    </v-list-item-content>
                  </v-list-item>
                </div>
              </v-expand-transition>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card>
              <v-card-text class="d-flex align-center flex-wrap py-3">
                <span class="mr-3 subtitle-2 font-weight-medium">Ansicht</span>
                <v-btn-toggle v-model="helpers.viewMode" mandatory dense>
                  <v-btn value="matrix" outlined>
                    <v-icon small left>mdi-table-large</v-icon>
                    Terminübersicht
                  </v-btn>
                  <v-btn value="timeSlots" outlined>
                    <v-icon small left>mdi-calendar-clock</v-icon>
                    Einteilungsübersicht
                  </v-btn>
                </v-btn-toggle>
                <v-spacer></v-spacer>
                <v-btn
                  v-if="canManageResponses"
                  color="primary"
                  depressed
                  :to="{
                    name: 'scheduling-request-details-manage-responses',
                    params: {
                      organizationId: $route.params.organizationId,
                      itemId: $route.params.itemId,
                    },
                  }"
                >
                  <v-icon left>mdi-pencil</v-icon>
                  Bearbeiten/Einteilen
                </v-btn>
                <v-menu
                  v-model="helpers.autoAssignment.menuOpen"
                  offset-y
                  :close-on-content-click="false"
                  :disabled="helpers.autoAssignment.loading"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-if="canManageResponses"
                      v-bind="attrs"
                      v-on="on"
                      color="orange"
                      dark
                      depressed
                      class="ml-2"
                      :loading="helpers.autoAssignment.loading"
                      :disabled="helpers.autoAssignment.loading"
                    >
                      <v-icon left>mdi-auto-fix</v-icon>
                      Automatisch einteilen
                    </v-btn>
                  </template>

                  <v-card min-width="360">
                    <v-card-text class="pb-2">
                      <div class="subtitle-2 mb-2">Einteilungsmodus</div>
                      <v-select
                        v-model="helpers.autoAssignment.selectedMode"
                        :items="assignmentModeItems"
                        item-text="label"
                        item-value="value"
                        dense
                        hide-details
                        outlined
                        class="matrix-assignment-menu-select"
                      ></v-select>
                    </v-card-text>
                    <v-card-actions class="mx-2">
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        :disabled="helpers.autoAssignment.loading"
                        @click="helpers.autoAssignment.menuOpen = false"
                      >
                        Abbrechen
                      </v-btn>
                      <v-btn
                        color="orange"
                        dark
                        depressed
                        :loading="helpers.autoAssignment.loading"
                        :disabled="helpers.autoAssignment.loading"
                        @click="runAutoAssignment"
                      >
                        Einteilung starten
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col v-if="helpers.viewMode === 'timeSlots'" cols="12">
            <!-- <v-card class="mb-3">
              <v-card-text>
                <v-row dense>
                  <v-col cols="12" sm="12" md="4">
                    <v-text-field
                      v-model="helpers.timeSlotFilters.search"
                      append-icon="mdi-magnify"
                      clearable
                      dense
                      outlined
                      hide-details
                      label="Terminoption suchen"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-select
                      v-model="helpers.timeSlotFilters.resourceStatus"
                      :items="timeSlotResourceStatusFilterItems"
                      item-text="label"
                      item-value="value"
                      dense
                      outlined
                      hide-details
                      label="Besetzbarkeit"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-select
                      v-model="helpers.timeSlotFilters.assignmentState"
                      :items="timeSlotAssignmentFilterItems"
                      item-text="label"
                      item-value="value"
                      dense
                      outlined
                      hide-details
                      label="Einteilung"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="2">
                    <v-select
                      v-model="helpers.timeSlotFilters.eventState"
                      :items="timeSlotEventFilterItems"
                      item-text="label"
                      item-value="value"
                      dense
                      outlined
                      hide-details
                      label="Veranstaltung"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card> -->

            <v-row class="match-height">
              <v-col
                v-for="timeSlot in timeSlotsOverviewOutput"
                :key="`overview-${timeSlot.id}`"
                cols="12"
                sm="12"
                md="6"
              >
                <v-card>
                  <v-card-title class="d-flex align-center py-3">
                    <div class="d-flex flex-column">
                      <span class="subtitle-1 font-weight-medium">{{
                        timeSlot.title
                      }}</span>
                      <span class="caption text--secondary"
                        >{{ timeSlot.startReadable }} bis
                        {{ timeSlot.endReadable }} Uhr</span
                      >
                    </div>
                    <v-spacer></v-spacer>
                    <v-chip
                      small
                      label
                      :color="
                        statusMetaByResourceStatus(timeSlot.resourceStatus)
                          .color
                      "
                      :class="
                        statusMetaByResourceStatus(timeSlot.resourceStatus)
                          .textClass
                      "
                    >
                      <v-icon left x-small>{{
                        statusMetaByResourceStatus(timeSlot.resourceStatus).icon
                      }}</v-icon>
                      {{
                        statusMetaByResourceStatus(timeSlot.resourceStatus)
                          .label
                      }}
                    </v-chip>
                  </v-card-title>
                  <v-divider></v-divider>

                  <v-card-text>
                    <response-statistics-card
                      small
                      marginClass="mb-2"
                      :totalConfirmations="
                        timeSlot.responses.confirmations.length
                      "
                      :totalAccepted="timeSlot.totalAccepted"
                      :totalIfNecessary="timeSlot.responses.ifNecessary.length"
                      :totalCancellations="
                        timeSlot.responses.cancellations.length
                      "
                      :totalNotInTown="timeSlot.responses.away.length"
                      :totalAwaitingResponse="
                        timeSlot.responses.awaitingResponse.length
                      "
                      :totalResponses="timeSlot.responses.all.length"
                    ></response-statistics-card>

                    <div class="subtitle-2 font-weight-medium mt-2">
                      Eingeteilte Personen
                    </div>
                    <v-chip-group v-if="timeSlot.assignedPersons.length" column>
                      <v-chip
                        v-for="person in timeSlot.assignedPersons"
                        :key="`${timeSlot.id}-${person.uid}`"
                        label
                        class="mb-2 font-weight-medium"
                      >
                        <!-- <v-avatar
                          left
                          rounded
                          size="20"
                          :color="
                            $vuetify.theme.dark
                              ? 'rgba(255, 255, 255, 0.18)'
                              : 'grey lighten-3'
                          "
                          class="font-weight-medium"
                          >{{ initials(person.displayName) }}</v-avatar
                        > -->
                        {{ person.displayName }}
                      </v-chip>
                    </v-chip-group>
                    <v-alert v-else dense text type="info" class="mt-2 mb-0">
                      Für diese Terminoption ist noch niemand eingeteilt.
                    </v-alert>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col
                v-if="timeSlotsOverviewOutput.length === 0"
                cols="12"
                class="text-center"
              >
                <v-alert dense outlined type="info" class="mb-0">
                  Keine Terminoptionen für den aktuellen Filter vorhanden.
                </v-alert>
              </v-col>
            </v-row>
          </v-col>

          <v-col v-show="helpers.viewMode === 'matrix'" cols="12">
            <request-responses-matrix
              :request="request"
              mode="view"
              :show-manage-button="canManageResponses"
              :enable-event-creation="canCreateEvents"
              :show-create-events-button="canCreateEvents"
              :create-events-button-count="
                helpers.eventCreation.selectedTimeSlotIds.length
              "
              :forced-event-created-time-slot-ids="
                helpers.eventCreation.createdEventTimeSlotIds
              "
              :selected-time-slot-ids="
                helpers.eventCreation.selectedTimeSlotIds
              "
              @stats-change="updateMatrixStats"
              @comments-change="updateResponseComments"
              @time-slots-change="updateTimeSlots"
              @selected-time-slot-ids-change="updateSelectedTimeSlotIds"
              @create-events-click="toggleEventCreationDialog()"
            ></request-responses-matrix>
          </v-col>
        </v-row>
      </div>
    </v-container>

    <section>
      <custom-dialog
        v-if="helpers.eventCreation.dialogModel"
        @close="toggleEventCreationDialog()"
        title="Veranstaltungen für Auswahl erstellen"
        text="Für ausgewählte Terminoptionen wird eine Veranstaltung erstellt. Daten werden aus der Abfrage und der ausgewählten Terminoption übernommen. Weitere Annpassungen können nach der Erstellung vorgenommen werden."
        width="m"
        :errorMessage="helpers.eventCreation.errorMessage"
        :btnAction="startEventCreation"
        :btnDisabled="!helpers.eventCreation.formIsValid"
        :loading="helpers.eventCreation.loading"
        btnText="Erstellen"
      >
        <template v-slot:content>
          <v-form
            v-model="helpers.eventCreation.formIsValid"
            @submit.prevent="startEventCreation()"
          >
            <v-row>
              <v-col cols="12" sm="12" md="12"
                ><v-chip label
                  >{{ helpers.eventCreation.selectedTimeSlotIds.length }}
                  Terminoptionen ausgewählt
                </v-chip>

                <v-alert border="left" text type="info" class="mt-3"
                  >Die Abfrage wird erst geschlossen, wenn für alle
                  Terminoptionen eine Veranstaltung erstellt wurde.</v-alert
                >

                <v-alert
                  v-if="notPossibleTimeSlotsInSelection"
                  border="left"
                  text
                  type="warning"
                  class="mt-2"
                  >In der Auswahl sind nicht besetzbare Terminoptionen.</v-alert
                >
              </v-col>
              <v-col cols="12" sm="12" md="8">
                <v-select
                  v-model="helpers.eventCreation.eventTypeId"
                  :items="eventTypes"
                  item-text="title"
                  item-value="meta.id"
                  :loading="helpers.eventCreation.eventTypeSelectorIsLoading"
                  outlined
                  :rules="[rules.required]"
                  label="Veranstaltungsart"
                  :disabled="helpers.eventCreation.loading"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="12" md="12">
                <v-checkbox
                  v-model="helpers.eventCreation.copyResponses"
                  label="Rückmeldungen aus Abfrage übernehmen"
                  hint="Rückmeldungen zu ausgewählten Terminoptionen werden in den Datensatz der Veranstaltung übernommen."
                  persistent-hint
                >
                </v-checkbox>
              </v-col>
              <v-col cols="12" sm="12" md="12">
                <v-checkbox
                  v-model="helpers.eventCreation.autoLimitAttendance"
                  label="Teilnahme automatisch auf Eingeteilte beschränken"
                  hint="Nach der Erstellung wird für jede neue Veranstaltung automatisch die Teilnahme auf eingeteilte Personen eingeschränkt."
                  persistent-hint
                >
                </v-checkbox>
              </v-col>
            </v-row>
          </v-form>
        </template>
      </custom-dialog>
    </section>

    <section v-if="checkIfSupport">
      <support-tools :sources="code"></support-tools>
    </section>
  </div>
</template>

<script>
import CustomDialog from "@/components/_systemwide/CustomDialog.vue";
import SupportTools from "@/components/_system/helpers/SupportTools.vue";
import RequestResponsesMatrix from "@/components/scheduling/RequestResponsesMatrix.vue";
import ResponseStatisticsCard from "@/components/scheduling/ResponseStatisticsCard.vue";
import { AVAILABILITY, SCHEDULING } from "@/store/modules.js";
import {
  CREATE_EVENTS_FROM_REQUEST,
  GET_EVENT_TYPES,
  RUN_REQUEST_AUTO_ASSIGNMENT,
} from "@/store/action-types.js";
import {
  AVAILABILITY_REQUESTS_RESPONSES_write,
  SCHEDULING_EVENTS_create,
} from "@/data/permission-types.js";

export default {
  name: "availability-request-details-tab-responses",
  components: {
    CustomDialog,
    SupportTools,
    RequestResponsesMatrix,
    ResponseStatisticsCard,
  },
  props: {
    request: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      rules: {
        required: (value) => !!value || "Feld ist erforderlich.",
      },
      matrixStats: {
        totalResponsesCount: {
          complete: 0,
          incomplete: 0,
          notResponded: 0,
        },
        possibleTimeSlots: 0,
        notPossibleTimeSlots: 0,
      },
      requestResponsesMatchedComments: [],
      timeSlotsData: [],
      timeSlotResourceStatusFilterItems: [
        { label: "Alle", value: "all" },
        { label: "Besetzbar", value: "target" },
        { label: "Besetzbar (Überhang)", value: "overhang" },
        { label: "Nicht besetzbar", value: "deficit" },
      ],
      timeSlotAssignmentFilterItems: [
        { label: "Alle", value: "all" },
        { label: "Mit Einteilung", value: "withAssigned" },
        { label: "Ohne Einteilung", value: "withoutAssigned" },
      ],
      timeSlotEventFilterItems: [
        { label: "Alle", value: "all" },
        { label: "Mit Veranstaltung", value: "eventCreated" },
        { label: "Ohne Veranstaltung", value: "withoutEvent" },
      ],
      assignmentModeItems: [
        {
          value: "prioritizeAtLeastOneAssignmentPerPerson",
          label: "Ausgewogen (mind. eine Einteilung)",
        },
        {
          value: "prioritizeMaxTimeSlots",
          label: "Maximale Besetzung je Terminoption",
        },
        {
          value: "prioritizeFirstComeFirstServe",
          label: "First Come, First Serve",
        },
      ],
      helpers: {
        viewMode: "matrix",
        showResponseComments: false,
        timeSlotFilters: {
          search: "",
          resourceStatus: "all",
          assignmentState: "all",
          eventState: "all",
        },
        autoAssignment: {
          selectedMode: "prioritizeAtLeastOneAssignmentPerPerson",
          menuOpen: false,
          loading: false,
        },
        eventCreation: {
          dialogModel: false,
          loading: false,
          formIsValid: false,
          errorMessage: "",
          eventTypeSelectorIsLoading: false,
          selectedTimeSlotIds: [],
          createdEventTimeSlotIds: [],
          eventTypeId: "",
          copyResponses: true,
          autoLimitAttendance: false,
        },
      },
    };
  },
  computed: {
    eventTypes() {
      return this.$store.state.scheduling.eventTypes || [];
    },
    canManageResponses() {
      return this.$store.getters["organization/checkPermissionByID"](
        AVAILABILITY_REQUESTS_RESPONSES_write,
      );
    },
    canCreateEvents() {
      return (
        this.canManageResponses &&
        this.$store.getters["organization/checkPermissionByID"](
          SCHEDULING_EVENTS_create,
        )
      );
    },
    notPossibleTimeSlotsInSelection() {
      const selected = this.helpers.eventCreation.selectedTimeSlotIds;
      if (!selected.length) {
        return false;
      }
      return this.timeSlotsData.some((timeSlot) => {
        return (
          selected.includes(timeSlot.id) &&
          timeSlot.resourceStatus?.status === "deficit"
        );
      });
    },
    timeSlotsOverview() {
      const slots = Array.isArray(this.timeSlotsData) ? this.timeSlotsData : [];

      return [...slots]
        .map((timeSlot) => {
          const responses = timeSlot.responses || {
            all: [],
            confirmations: [],
            ifNecessary: [],
          };

          const assignedPersons = (responses.all || [])
            .filter((response) => response.status?.isAccepted === true)
            .map((response) => {
              return {
                uid: response.user?.uid || response.meta?.uid || "",
                displayName:
                  response.user?.displayName ||
                  response.meta?.uid ||
                  "Unbekannt",
              };
            });

          return {
            ...timeSlot,
            startReadable: this.getReadableDateLabel(timeSlot.start),
            endReadable: this.getReadableDateLabel(timeSlot.end),
            assignedPersons,
            totalAccepted: assignedPersons.length,
          };
        })
        .sort((a, b) => {
          const startA = this.getDateAsMillis(a.start);
          const startB = this.getDateAsMillis(b.start);
          if (startA !== startB) {
            return startA - startB;
          }
          return String(a.id).localeCompare(String(b.id));
        });
    },
    timeSlotsOverviewOutput() {
      const search = (this.helpers.timeSlotFilters.search || "")
        .toLowerCase()
        .trim();
      const resourceStatusFilter = this.helpers.timeSlotFilters.resourceStatus;
      const assignmentFilter = this.helpers.timeSlotFilters.assignmentState;
      const eventStateFilter = this.helpers.timeSlotFilters.eventState;

      return this.timeSlotsOverview.filter((timeSlot) => {
        if (
          search &&
          !(timeSlot.title || "").toLowerCase().includes(search) &&
          !(timeSlot.startReadable || "").toLowerCase().includes(search)
        ) {
          return false;
        }

        if (
          resourceStatusFilter !== "all" &&
          timeSlot.resourceStatus?.status !== resourceStatusFilter
        ) {
          return false;
        }

        if (
          assignmentFilter === "withAssigned" &&
          timeSlot.assignedPersons.length === 0
        ) {
          return false;
        }

        if (
          assignmentFilter === "withoutAssigned" &&
          timeSlot.assignedPersons.length > 0
        ) {
          return false;
        }

        if (eventStateFilter === "eventCreated" && !timeSlot.eventCreated) {
          return false;
        }

        if (eventStateFilter === "withoutEvent" && timeSlot.eventCreated) {
          return false;
        }

        return true;
      });
    },
    checkIfSupport() {
      return this.$store.getters["organization/checkIfSupport"];
    },
    code() {
      return [{ title: "request", data: this.request }];
    },
  },
  created() {
    this.syncAutoAssignmentModeWithRequest();
  },
  watch: {
    request: {
      handler() {
        this.syncAutoAssignmentModeWithRequest();
      },
      immediate: true,
      deep: false,
    },
  },
  methods: {
    syncAutoAssignmentModeWithRequest() {
      const requestDefaultMode =
        this.request?.config?.defaultAutoAssignmentMode;
      const validModes = this.assignmentModeItems.map((item) => item.value);
      this.helpers.autoAssignment.selectedMode = validModes.includes(
        requestDefaultMode,
      )
        ? requestDefaultMode
        : "prioritizeAtLeastOneAssignmentPerPerson";
    },
    async runAutoAssignment() {
      this.helpers.autoAssignment.loading = true;
      let isSuccessful = true;

      try {
        await this.$store.dispatch(
          `${AVAILABILITY}${RUN_REQUEST_AUTO_ASSIGNMENT}`,
          {
            organizationId: this.$route.params.organizationId,
            requestId: this.$route.params.itemId,
            createEvents: false,
            mode: this.helpers.autoAssignment.selectedMode,
          },
        );
      } catch (error) {
        isSuccessful = false;
        console.error("Error in runAutoAssignment:", error);
      } finally {
        this.helpers.autoAssignment.loading = false;
      }

      if (isSuccessful) {
        this.helpers.autoAssignment.menuOpen = false;
      }
    },
    getDateAsMillis(dateInput) {
      if (!dateInput) {
        return Number.MAX_SAFE_INTEGER;
      }

      if (dateInput.timestampToDate instanceof Date) {
        return dateInput.timestampToDate.getTime();
      }

      if (dateInput.timestamp?.toDate) {
        return dateInput.timestamp.toDate().getTime();
      }

      if (dateInput.toDate) {
        return dateInput.toDate().getTime();
      }

      const parsed = new Date(dateInput).getTime();
      return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
    },
    getReadableDateLabel(dateInput) {
      if (dateInput?.timestampToReadableDate) {
        return dateInput.timestampToReadableDate;
      }

      const timestamp = this.getDateAsMillis(dateInput);
      if (timestamp === Number.MAX_SAFE_INTEGER) {
        return "-";
      }

      const options = {
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Date(timestamp).toLocaleDateString("de-DE", options);
    },
    statusMetaByResourceStatus(resourceStatus) {
      switch (resourceStatus?.status) {
        case "target":
          return {
            label: "Besetzbar",
            icon: "mdi-check-circle",
            color: "green lighten-4",
            textClass: "green--text text--darken-3",
          };
        case "overhang":
          return {
            label: "Besetzbar (Überhang)",
            icon: "mdi-arrow-up-circle",
            color: "orange lighten-4",
            textClass: "orange--text text--darken-3",
          };
        case "deficit":
        default:
          return {
            label: "Nicht besetzbar",
            icon: "mdi-close-circle",
            color: "red lighten-4",
            textClass: "red--text text--darken-3",
          };
      }
    },
    async loadEventTypes() {
      this.helpers.eventCreation.eventTypeSelectorIsLoading = true;
      try {
        await this.$store.dispatch(`${SCHEDULING}${GET_EVENT_TYPES}`, {
          organizationId: this.$route.params.organizationId,
        });
      } finally {
        this.helpers.eventCreation.eventTypeSelectorIsLoading = false;
      }
    },
    async toggleEventCreationDialog() {
      this.helpers.eventCreation.dialogModel =
        !this.helpers.eventCreation.dialogModel;
      this.helpers.eventCreation.errorMessage = "";

      if (this.helpers.eventCreation.dialogModel) {
        await this.loadEventTypes();
      }
    },
    updateSelectedTimeSlotIds(newSelection) {
      const selectedIds = Array.isArray(newSelection) ? newSelection : [];
      const availableIds = new Set(this.timeSlotsData.map((slot) => slot.id));
      this.helpers.eventCreation.selectedTimeSlotIds = selectedIds.filter(
        (id) => availableIds.has(id),
      );
    },
    updateTimeSlots(timeSlots) {
      this.timeSlotsData = Array.isArray(timeSlots) ? timeSlots : [];
      this.updateSelectedTimeSlotIds(
        this.helpers.eventCreation.selectedTimeSlotIds,
      );
    },
    async startEventCreation() {
      if (!this.helpers.eventCreation.formIsValid) {
        return;
      }

      this.helpers.eventCreation.loading = true;
      this.helpers.eventCreation.errorMessage = "";

      try {
        const result = await this.$store.dispatch(
          `${AVAILABILITY}${CREATE_EVENTS_FROM_REQUEST}`,
          {
            organizationId: this.$route.params.organizationId,
            requestId: this.$route.params.itemId,
            timeSlotIds: this.helpers.eventCreation.selectedTimeSlotIds,
            eventTypeId: this.helpers.eventCreation.eventTypeId,
            copyResponses: this.helpers.eventCreation.copyResponses,
            autoLimitAttendance: this.helpers.eventCreation.autoLimitAttendance,
          },
        );

        const createdEventIds = result?.data?.createdEventIds || [];
        this.helpers.eventCreation.createdEventTimeSlotIds = Array.from(
          new Set([
            ...this.helpers.eventCreation.createdEventTimeSlotIds,
            ...createdEventIds,
          ]),
        );

        this.helpers.eventCreation.dialogModel = false;
        this.helpers.eventCreation.selectedTimeSlotIds = [];
        this.helpers.eventCreation.eventTypeId = "";
        this.helpers.eventCreation.copyResponses = true;
        this.helpers.eventCreation.autoLimitAttendance = false;
      } catch (error) {
        this.helpers.eventCreation.errorMessage =
          error?.message || "Fehler beim Erstellen der Veranstaltungen.";
      } finally {
        this.helpers.eventCreation.loading = false;
      }
    },
    initials(fullName) {
      if (!fullName) {
        return "?";
      }
      const namesArray = fullName.trim().split(" ");
      if (namesArray.length === 1) {
        return `${namesArray[0].charAt(0)}`;
      }
      return `${namesArray[0].charAt(0)}${namesArray[
        namesArray.length - 1
      ].charAt(0)}`;
    },
    updateMatrixStats(stats) {
      this.matrixStats = {
        ...this.matrixStats,
        ...(stats || {}),
      };
    },
    updateResponseComments(comments) {
      this.requestResponsesMatchedComments = comments || [];
    },
  },
};
</script>
